import React, { useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinAgencyButton({ className = "", label = "Join the Agency", testid = "join-agency-button" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", socials: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setDone(false);
      setForm({ name: "", email: "", socials: "" });
    }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.socials) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, form);
      setDone(true);
      toast.success("Application received! We'll be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-none border-0 border-b border-white/20 bg-transparent py-4 text-base font-light tracking-wide text-white placeholder-white/30 outline-none transition-colors focus:border-white";

  return (
    <>
      <button data-testid={testid} onClick={() => setOpen(true)} className={className}>
        {label}
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              className="fixed inset-0 z-[200] flex items-center justify-center px-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={close} />
              <motion.div
                data-testid="join-agency-modal"
                initial={{ opacity: 0, y: 30, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.98 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 w-full max-w-md border border-white/15 bg-[#0d0d0d] p-8 md:p-10"
              >
                <button
                  onClick={close}
                  data-testid="join-agency-close"
                  className="absolute right-5 top-5 text-white/40 transition-colors hover:text-white"
                >
                  <X size={20} strokeWidth={1.4} />
                </button>

                {done ? (
                  <div className="flex flex-col items-center py-6 text-center" data-testid="join-agency-success">
                    <CheckCircle2 size={40} strokeWidth={1} className="text-white" />
                    <h3 className="font-serif-lux mt-6 text-3xl font-light tracking-tight text-white">You're in the queue.</h3>
                    <p className="mt-4 text-sm font-light leading-relaxed text-white/60">
                      Thanks, {form.name.split(" ")[0]}. Our team will review your profile and reach out soon.
                    </p>
                    <button onClick={close} className="mt-8 text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-white">
                      Close
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mb-8 flex items-center gap-2 text-white/50">
                      <Sparkles size={16} strokeWidth={1.4} />
                      <span className="text-[10px] uppercase tracking-[0.35em]">Creator Application</span>
                    </div>
                    <h3 className="font-serif-lux text-4xl font-light leading-none tracking-tight text-white">
                      Join the<br />Agency.
                    </h3>
                    <p className="mt-4 text-sm font-light leading-relaxed text-white/50">
                      Tell us who you are and where to find you. We're always looking for the next voice.
                    </p>
                    <form onSubmit={submit} className="mt-8 space-y-6" data-testid="join-agency-form">
                      <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Full Name" data-testid="join-name" className={inputCls} />
                      <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="Email Address" data-testid="join-email" className={inputCls} />
                      <input type="text" name="socials" value={form.socials} onChange={handleChange} placeholder="Social handles (e.g. @you on IG, TikTok)" data-testid="join-socials" className={inputCls} />
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="join-submit-button"
                        className="group inline-flex w-full items-center justify-center gap-3 rounded-none bg-white px-8 py-4 text-[11px] uppercase tracking-[0.3em] text-black transition-colors hover:bg-white/80 disabled:opacity-50"
                      >
                        {submitting ? "Sending" : "Submit Application"}
                        <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </button>
                    </form>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
